import React from 'react';
import { Briefcase, Car, CheckCircle2, Clock, Hourglass, Lightbulb, MapPinOff } from 'lucide-react';
import type { RoutySlotAuditArtifact, SlotAuditLine } from '../../business/routy/routyArtifacts';
import { formatDurationMinutes } from '../../utils/timeUtils';

interface SlotAuditCardProps {
  artifact: RoutySlotAuditArtifact;
}

const LINE_ICONS: Record<SlotAuditLine['kind'], React.ComponentType<{ className?: string }>> = {
  drive: Car,
  buffer: Hourglass,
  visit: Briefcase,
  window: Clock,
};

/**
 * The minute-by-minute answer to "why won't this window take the job?".
 *
 * Every row is a real cost against the window — drive in, buffer, the visit,
 * drive out — so the verdict lands as arithmetic the operator can check and
 * argue with, rather than a system saying no.
 */
const SlotAuditCard: React.FC<SlotAuditCardProps> = ({ artifact }) => {
  const budget = artifact.lines.filter((l) => l.kind !== 'window');
  // The bar is capped at the window: an overflowing budget is communicated by
  // the shortfall number, not by a bar that runs off the card.
  const usedPct = artifact.windowMinutes
    ? Math.min(100, Math.round((artifact.requiredMinutes / artifact.windowMinutes) * 100))
    : 0;

  return (
    <div className="ml-10 rounded-lg bg-surface border border-ink-200 shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 bg-ink-50 border-b border-ink-200">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-ink-900">{artifact.slotLabel}</span>
          <span className="flex-shrink-0 text-2xs font-medium text-ink-500">{artifact.dateLabel}</span>
        </div>
        {(artifact.afterTitle || artifact.beforeTitle) && (
          <p className="mt-0.5 text-2xs text-ink-500 truncate">
            {artifact.afterTitle && `after ${artifact.afterTitle}`}
            {artifact.afterTitle && artifact.beforeTitle && ' · '}
            {artifact.beforeTitle && `before ${artifact.beforeTitle}`}
          </p>
        )}
      </div>

      <div className="p-3 space-y-3">
        <div
          className={`flex items-start gap-2.5 rounded-lg border p-3 ${
            artifact.fits ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
          }`}
        >
          {artifact.fits ? (
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
          ) : (
            <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
          )}
          <div className="min-w-0">
            <p
              className={`text-sm font-semibold leading-snug ${
                artifact.fits ? 'text-emerald-900' : 'text-amber-900'
              }`}
            >
              {artifact.verdict}
            </p>
            <p className={`mt-1 text-xs ${artifact.fits ? 'text-emerald-800/90' : 'text-amber-800/90'}`}>
              The window holds {formatDurationMinutes(artifact.windowMinutes, { short: true })} and{' '}
              {artifact.serviceName ? `a ${artifact.serviceName}` : 'the work'} needs{' '}
              {formatDurationMinutes(artifact.requiredMinutes, { short: true })}.
            </p>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between gap-2">
            <p className="text-2xs font-medium uppercase tracking-wide text-ink-400">Where the time goes</p>
            <span className="text-2xs font-medium text-ink-500">
              {formatDurationMinutes(artifact.requiredMinutes, { short: true })} of{' '}
              {formatDurationMinutes(artifact.windowMinutes, { short: true })}
            </span>
          </div>
          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-ink-100">
            <div
              className={`h-full rounded-full ${artifact.fits ? 'bg-emerald-500' : 'bg-amber-500'}`}
              style={{ width: `${usedPct}%` }}
            />
          </div>

          <div className="mt-2 space-y-1.5">
            {budget.map((line, i) => {
              const Icon = LINE_ICONS[line.kind];
              return (
                <div key={`${line.label}-${i}`} className="flex items-start gap-2.5">
                  <Icon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-ink-400" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-xs font-medium text-ink-800 truncate">{line.label}</span>
                      <span className="flex-shrink-0 text-xs font-semibold text-ink-900">
                        {formatDurationMinutes(line.minutes, { short: true })}
                      </span>
                    </div>
                    {line.detail && (
                      <p className="text-2xs text-ink-500 truncate">
                        {line.detail}
                        {line.estimated ? ' · estimated' : ''}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Without this the arithmetic above reads as exact when one of its
            inputs was a guess: an unlocatable neighbour sends the drive leg
            back to the day's starting point instead of that stop. */}
        {artifact.unmeasuredNeighbors && artifact.unmeasuredNeighbors.length > 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5">
            <MapPinOff className="mt-px h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
            <p className="text-2xs leading-relaxed text-amber-800">
              We can&apos;t locate {artifact.unmeasuredNeighbors.join(' or ')}, so the drive time above was
              measured from your starting point instead of that stop. Completing that address makes these
              minutes real.
            </p>
          </div>
        )}

        {artifact.remedies && artifact.remedies.length > 0 && (
          <div className="rounded-lg border border-ink-200 bg-ink-50/60 p-2.5">
            <p className="flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wide text-ink-500">
              <Lightbulb className="h-3.5 w-3.5 text-ink-400" />
              What would make it fit
            </p>
            <ul className="mt-1.5 space-y-1">
              {artifact.remedies.map((r) => (
                <li key={r} className="text-xs leading-relaxed text-ink-700">
                  {r}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default SlotAuditCard;
